import {LifeBuoy, Waves} from 'lucide-react'
import type { CandidateQualification } from '@/hooks/useAvailableCandidates'

/**
 * Écusson héraldique pour profils candidats.
 *
 * Forme bouclier SVG avec :
 *  - Dégradé radial silo-coloré (rouge BNSSA / bleus MNS-BPJEPS-BEESAN-STAPS)
 *  - Icône métier au centre (LifeBuoy pour BNSSA, Waves pour les autres)
 *  - Reflet brillant en haut pour effet médaille
 *  - Liseré doré optionnel pour les profils vérifiés (AE validé / diplôme)
 *  - Sigle de la qualification gravé sous l'écusson en monogramme
 */

type ShieldVariant = 'dark' | 'light'

interface Props {
  qualification: CandidateQualification
  /** Si true → liseré doré (profil vérifié AE / diplôme) */
  verified?: boolean
  /** Taille de l'écusson (en px). Default 64. */
  size?: number
  /** Mode visuel : 'dark' (fond sombre /home) ou 'light' (carte blanche /disponibles) */
  variant?: ShieldVariant
  /** Affiche le sigle gravé sous l'écusson */
  showLabel?: boolean
  className?: string
}

/** Palette par qualification — dégradés héraldiques, charte BNSSA/MNS respectée.
 *  Tous les diplômes MNS (MNS, BPJEPS AAN, BEESAN, STAPS) partagent la même
 *  teinte indigo « BPJEPS » pour une lecture cohérente côté recruteur.
 */
const MNS_PALETTE = {
  from: '#6366f1', // indigo-500
  to: '#312e81', // indigo-900
  ring: '#c7d2fe', // indigo-200
}

const PALETTE: Record<
  CandidateQualification,
  { from: string; to: string; ring: string }
> = {
  BNSSA: {
    from: '#ef4444', // red-500
    to: '#991b1b', // red-800
    ring: '#fecaca', // red-200
  },
  MNS: { ...MNS_PALETTE },
  'BPJEPS AAN': { ...MNS_PALETTE },
  BEESAN: { ...MNS_PALETTE },
  STAPS: { ...MNS_PALETTE },
}

export default function CandidateShield({
  qualification,
  verified = false,
  size = 64,
  variant = 'dark',
  showLabel = false,
  className = '',
}: Props) {
  const palette = PALETTE[qualification] ?? PALETTE.BNSSA
  const isBnssa = qualification === 'BNSSA'
  const Icon = isBnssa ? LifeBuoy : Waves
  const gradientId = `shield-grad-${qualification.replace(/\s/g, '-')}`
  const glossId = `shield-gloss-${qualification.replace(/\s/g, '-')}`

  const iconSize = Math.round(size * 0.42)

  // Couleur du sigle selon variant
  const labelClass =
    variant === 'dark'
      ? 'text-white/70'
      : 'text-gray-600'

  return (
    <div className={`inline-flex flex-col items-center gap-1.5 ${className}`}>
      <div
        className="relative flex-shrink-0"
        style={{
          width: size,
          height: size,
        }}
      >
        {/* Bouclier SVG */}
        <svg
          viewBox="0 0 64 72"
          width={size}
          height={size}
          aria-hidden="true"
          className="absolute inset-0"
        >
          <defs>
            {/* Dégradé radial principal */}
            <radialGradient id={gradientId} cx="50%" cy="35%" r="70%">
              <stop offset="0%" stopColor={palette.from} stopOpacity="1" />
              <stop offset="100%" stopColor={palette.to} stopOpacity="1" />
            </radialGradient>
            {/* Reflet brillant haut */}
            <linearGradient id={glossId} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
              <stop offset="60%" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Forme bouclier classique : pointe en bas, épaules en haut */}
          <path
            d="M32 2 L60 10 L60 34 C60 52 48 64 32 70 C16 64 4 52 4 34 L4 10 Z"
            fill={`url(#${gradientId})`}
            stroke={verified ? '#fbbf24' : palette.ring}
            strokeWidth={verified ? 2 : 1.5}
            strokeOpacity={verified ? 0.95 : 0.4}
          />

          {/* Reflet brillant supérieur (effet médaille) */}
          <path
            d="M32 4 L58 11 L58 28 C58 32 50 36 32 36 C14 36 6 32 6 28 L6 11 Z"
            fill={`url(#${glossId})`}
          />

          {/* Liseré doré intérieur si vérifié */}
          {verified && (
            <path
              d="M32 7 L56 14 L56 33 C56 49 46 60 32 65 C18 60 8 49 8 33 L8 14 Z"
              fill="none"
              stroke="#fde68a"
              strokeWidth="0.8"
              strokeOpacity="0.6"
            />
          )}
        </svg>

        {/* Icône métier centrée */}
        <div
          className="absolute inset-0 flex items-center justify-center text-white"
          style={{ paddingTop: Math.round(size * 0.06) }}
        >
          <Icon
            width={iconSize}
            height={iconSize}
            strokeWidth={2.2}
            aria-hidden="true"
          />
        </div>

        {/* Petit point doré "vérifié" en bas à droite */}
        {verified && (
          <span
            className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-amber-400 border-2 border-white shadow"
            aria-label="Profil vérifié"
            title="Profil vérifié"
          />
        )}
      </div>

      {/* Sigle gravé sous l'écusson */}
      {showLabel && (
        <span
          className={`text-[9px] font-black tracking-[0.18em] uppercase ${labelClass}`}
        >
          {qualification}
        </span>
      )}
    </div>
  )
}
